import { ApiError } from 'aws-amplify/api';
import React, { useState, useEffect } from 'react';
import Chart from 'chart.js/auto';
import { CategoryScale } from 'chart.js';
import { Line } from 'react-chartjs-2';
import CodeBlock from '../mui-components/CodeBlock';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Layout from '../mui-components/Layout';
import { pageTitles } from '../data/pageTitles';
import { Box, Button, IconButton, MenuItem, Select, Typography } from '@mui/material';
import { useGlobalContext } from '../context/GlobalStore';
import AddApiKeyModal from '../mui-components/ApiKeys/AddApiKeyModal';
import AlertModal from '../mui-components/AlertModal';
import ErrorAlertModal from '../mui-components/ErrorAlertModal';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Loading from '../mui-components/Loading';
import apiClient from '../utils/apiClient';

Chart.register(CategoryScale);

export default function ApiKeys({ getAccount }) {
  document.title = pageTitles.apiKeys;
  const { state, setAccount } = useGlobalContext();
  const [usageChartData, setUsageChartData] = useState({ labels: [], datasets: [] });
  const [remainingChartData, setRemainingChartData] = useState({ labels: [], datasets: [] });
  const [isShowKey, setIsShowKey] = useState(false);
  const placeholder = '*****************************';
  const [openAddApiKey, setOpenAddApiKey] = useState(false);
  const [allowAddApiKey, setAllowAddApiKey] = useState(true);
  const [addApiKeyMessage, setAddApiKeyMessage] = useState("");
  const [newApiKey, setNewApiKey] = useState({
    api_key_name: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [openErrorAlert, setOpenErrorAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertTitle, setAlertTitle] = useState("");
  const [openAlert, setOpenAlert] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  useEffect(() => {
    if (!state?.policies?.hasOwnProperty("Admin")) {
      setAllowAddApiKey(false);
      setAddApiKeyMessage("Only administrators can add new API keys.")
    } else {
      const adminPolicies = Object.values(state.policies["Admin"])[0]
      if (!adminPolicies.includes("admin:CreateApiKey")) {
        setAllowAddApiKey(false);
        setAddApiKeyMessage("Only administrators can add new API keys.")
      } else if (state.account.pylo_accounts.length >= 10) {
        setAllowAddApiKey(false);
        setAddApiKeyMessage("Maximum number of API keys reached. Please contact support.")
      } else {
        setAllowAddApiKey(true);
        setAddApiKeyMessage("") 
      }
    }
  }, [state.account])

  useEffect(() => {
    if (state.apiKey && state.account?.usage_data?.[state.apiKey.key_id]) {
      const usageData = state.account.usage_data[state.apiKey.key_id];
      // data is now [date, used, remaining]
      const labels = usageData.map(item => item[0]);
      
      setUsageChartData({
        labels: labels,
        datasets: [
          {
            label: 'Used',
            data: usageData.map(item => item[1]),
            borderColor: 'rgb(255, 99, 132)',
            backgroundColor: 'rgba(255, 99, 132, 0.5)',
          },
        ],
      });

      setRemainingChartData({
        labels: labels,
        datasets: [
          {
            label: 'Remaining',
            data: usageData.map(item => item[2]),
            borderColor: 'rgb(54, 162, 235)',
            backgroundColor: 'rgba(54, 162, 235, 0.5)',
          },
        ],
      });
    } else {
      // Clear charts if no data
      setUsageChartData({ labels: [], datasets: [] });
      setRemainingChartData({ labels: [], datasets: [] });
    }
  }, [state.apiKey, state.account]);

  async function createApiKey(e) {
    e.preventDefault();
    setIsLoading(true);
    const accountId = state.account.pylo_accounts[0]["user_group_id"];
    try {

      const response = await apiClient.post(
        '/apiKeys',
        {
          action: 'create',
          access_token: state.token.toString(),
          account_id: accountId,
          customer_name: newApiKey.customer_name,
          api_key_name: newApiKey.api_key_name,
          email: newApiKey.email
        },
        {
          retryOptions: { maxRetries: 0 }
        },
        true
      );

      if (response) {
        // update account data with new api key
        setSnackbarMessage(`API key ${newApiKey.api_key_name} created successfully.`);
        setOpenSnackbar(true);
        await getAccount(state.token.toString());
      }
    } catch (error) {
      let errorMessage = '';
      if (error instanceof ApiError) {
        if (error.response) {
          const { statusCode, headers, body} = error.response;
          console.log(`Received ${statusCode} error response with payload ${body}`);
          errorMessage = JSON.parse(body);
        }
      } else {
        // standard error
        errorMessage = error.message;
      }
      setAlertMessage(`Error creating API key: ${errorMessage}`);
      setAlertTitle("Error");
      setOpenErrorAlert(true);
      setIsLoading(false);
    }
    setNewApiKey({
      api_key_name: "",
    })
    toggleAddApiKeyModal();
    setIsLoading(false);
  }

  const handleAddApiKey = (e) => {
    createApiKey(e);
  }

  async function rotateKey(api_key) {
    try {
      const accountId = state.account.pylo_accounts.find(
        (a) => a.key_id === api_key
      )['id'];
    const response = await apiClient.post(
      '/apiKeys',
      {
        action: 'rotate',
        access_token: state.token.toString(),
        account_id: accountId,
      },
      {
        retryOptions: { maxRetries: 0 }
      },
      true
    );
      const copy = { ...state.account };
      const a = copy.pylo_accounts.find((a) => a.key_id === api_key);
      const usageDataCopy = copy.usage_data;
      delete usageDataCopy[a.key_id];
      a['key_id'] = response['key_id'];
      a['api_key'] = response['api_key'];
      usageDataCopy[response['key_id']] = [];
      copy['usage_data'] = usageDataCopy;
      setAccount(copy);
    } catch (e) {
      console.log('POST call failed: ', JSON.parse(e.response.body));
    }
  }

  const toggleVisiblity = () => {
    setIsShowKey(!isShowKey);
  };

  const toggleAddApiKeyModal = () => {
    setOpenAddApiKey(!openAddApiKey);
  };

  if (!state.apiKey || !state.account) return (<Layout title=""></Layout>)

  return (
    <Layout title="API Keys">
      <Snackbar
        open={openSnackbar}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="success" variant="filled" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      {openAddApiKey && (
        <AddApiKeyModal
          open={true}
          handleClose={toggleAddApiKeyModal}
          handleAddApiKey={handleAddApiKey}
          newApiKey={newApiKey}
          setNewApiKey={setNewApiKey}
          isLoading={isLoading}
        />
      )}
      <ErrorAlertModal
        open={openErrorAlert}
        handleClose={() => setOpenErrorAlert(false)}
        message={alertMessage}
        title={alertTitle}
      />
      <AlertModal
        open={openAlert}
        handleClose={() => setOpenAlert(false)}
        message={alertMessage}
        title={alertTitle}
        isHtml={true}
      />
      {state.isDeviceLoading ? (
        <Loading />
      ) : (<div className="col-75">
        <Box display="flex" alignItems="center" gap={3}>
          <Box mt={3} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
            <Button 
              variant="contained" 
              disabled={!allowAddApiKey}
              onClick={toggleAddApiKeyModal}
            >
              Add API Key
            </Button>
            <Typography variant="caption" color="textSecondary" display="block" sx={{ maxWidth: 250 }}>
              {addApiKeyMessage}
            </Typography>
          </Box>
        </Box><br />

        <Box display="flex">
          <CodeBlock
            minWidth={400}
            text={isShowKey ? state.account.pylo_accounts.find((a) => a.key_id === state.apiKey.key_id).api_key : placeholder}
          />
          <IconButton onClick={() => toggleVisiblity()}>
            {isShowKey ? <VisibilityOffIcon /> : <RemoveRedEyeIcon />}
          </IconButton>
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: '20px',
            marginTop: '50px',
            flexWrap: 'wrap'
          }}
        >
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <Typography variant="h6" align="center">Usage</Typography>
            {usageChartData.datasets.length > 0 && usageChartData.datasets[0].data.length > 0 && <Line data={usageChartData} />}
          </Box>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <Typography variant="h6" align="center">Remaining Quota</Typography>
            {remainingChartData.datasets.length > 0 && remainingChartData.datasets[0].data.length > 0 && <Line data={remainingChartData} />}
          </Box>
        </Box>
      </div>)}
    </Layout>
  );
}
